import React, { useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from 'sonner';
import axios, { initializeCsrf } from './lib/axios';

// Import configured axios (CSRF + credentials) - must be first
import './lib/axios';

// Boost Config Provider
import { BoostConfigProvider } from './components/BoostComponents';

// Pages
import HomePage from './pages/HomePage';
import MarketplacePage from './pages/MarketplacePage';
import CategoriesPage from './pages/CategoriesPage';
import ListingDetailPage from './pages/ListingDetailPage';
import CreateListingPage from './pages/CreateListingPage';
import ProfilePage from './pages/ProfilePage';
import MyListingsPage from './pages/MyListingsPage';
import MyBidsPage from './pages/MyBidsPage';
import AuthPage from './pages/AuthPage';
import FavoritesPage from './pages/FavoritesPage';
import NotificationsPage from './pages/NotificationsPage';
import CurrencyConverterPage from './pages/CurrencyConverterPage';
import AdminPageNew from './pages/AdminPageNew';
import AdminLoginPage from './pages/AdminLoginPage';
import TermsPage from './pages/TermsPage';
import FAQPage from './pages/FAQPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PricingPage from './pages/PricingPage';
import SellerAnalyticsPage from './pages/SellerAnalyticsPage';
import SellerProfilePage from './pages/SellerProfilePage';
import MessagesPage from './pages/MessagesPage';
import HotspotsPage from './pages/HotspotsPage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import CreateEventPage from './pages/CreateEventPage';
import MyEventsPage from './pages/MyEventsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import NotFoundPage from './pages/NotFoundPage';
import TradeProposalsPage from './pages/TradeProposalsPage';
import StartSellingPage from './pages/StartSellingPage';
import PromoteListingPage from './pages/PromoteListingPage';
import SafariDropdownTestPage from './pages/SafariDropdownTestPage';
import SafariDropdownTestV2 from './pages/SafariDropdownTestV2';
import CategoryTestPage from './pages/CategoryTestPage';
import HeroOptionsPage from './pages/HeroOptionsPage';
import InstallAppPage from './pages/InstallAppPage';
import AboutPage from './pages/AboutPage';
import ComingSoonPage from './pages/ComingSoonPage';

// Components
import ScrollToTop from './components/ScrollToTop';
import ScrollToTopButton from './components/ScrollToTopButton';
import GoogleReviewPopup from './components/GoogleReviewPopup';
import ShareWinPopup, { PENDING_SHARE_ACTION_KEY } from './components/ShareWinPopup';
import IslandSelectorPopup from './components/IslandSelectorPopup';
import MobileBottomNav from './components/MobileBottomNav';
import PushNotificationPrompt from './components/PushNotificationPrompt';
import InstallAppPopup from './components/InstallAppPopup';
import CookieNotice from './components/CookieNotice';
import ReferralTracker from './components/ReferralTracker';
import WelcomeFlow from './components/WelcomeFlow';

// Contexts - All providers and hooks from centralized context files
import {
  ThemeProvider,
  useTheme,
  LanguageProvider,
  useLanguage,
  CurrencyProvider,
  useCurrency,
  AuthProvider,
  useAuth,
  WebSocketProvider,
  useWebSocket,
  NotificationProvider,
  useNotifications,
  IslandProvider,
  useIsland
} from './contexts';

// Re-export hooks for backward compatibility with existing imports
export { useTheme, useLanguage, useCurrency, useAuth, useWebSocket, useNotifications, useIsland };

// Utilities
import { formatCurrency, formatPrice } from './utils/currencyFormatter';
export { formatCurrency, formatPrice };

// Logo URLs for theme support
export const LOGO_URL_DARK = '/logo-slogan.png';
export const LOGO_URL_LIGHT = '/logo-slogan-dark.png';
// Header-specific logos (larger text for readability) - without tagline
export const HEADER_LOGO_DARK = '/logo.png';
export const HEADER_LOGO_LIGHT = '/logo.png';
// Hero section icon (just the icon, no text)
export const HERO_ICON_URL = 'https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/c4j2ei39_ICONYBS.png';
// Default export for backward compatibility
export const LOGO_URL = LOGO_URL_DARK;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// HomePageWrapper - Shows ComingSoonPage if enabled, otherwise HomePage
const HomePageWrapper = () => {
  const [showComingSoon, setShowComingSoon] = useState(null); // null = loading
  
  useEffect(() => {
    const checkComingSoon = async () => {
      try {
        const response = await axios.get(`${API}/admin/coming-soon`);
        setShowComingSoon(response.data?.enabled === true);
      } catch (error) {
        console.error('Error checking coming soon status:', error);
        setShowComingSoon(false); // Default to normal homepage on error
      }
    };
    checkComingSoon();
  }, []);
  
  // Loading state
  if (showComingSoon === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  return showComingSoon ? <ComingSoonPage /> : <HomePage />;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <CurrencyProvider>
          <AuthProvider>
            <WebSocketProvider>
              <NotificationProvider>
                <IslandProvider>
                  <BoostConfigProvider>
                    <AppContent />
                  </BoostConfigProvider>
                </IslandProvider>
              </NotificationProvider>
            </WebSocketProvider>
          </AuthProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [forceShowShareWin, setForceShowShareWin] = useState(false);

  // Initialize CSRF token early - important for iOS Safari
  useEffect(() => {
    initializeCsrf().catch(err => {
      console.warn('CSRF initialization failed:', err);
    });
  }, []);

  // Check for pending share action after user logs in
  // BUT: Wait for WelcomeFlow to complete first if user is new
  useEffect(() => {
    if (user) {
      const pendingShareAction = localStorage.getItem(PENDING_SHARE_ACTION_KEY);
      if (pendingShareAction === 'true') {
        // If new user, DON'T show ShareWin yet - WelcomeFlow has priority
        // The WelcomeFlow will trigger ShareWin after completion
        if (user.is_new_user === true) {
          // Keep the flag, WelcomeFlow will handle it
          return;
        }
        
        // Existing user (not new) - show ShareWin immediately
        localStorage.removeItem(PENDING_SHARE_ACTION_KEY);
        const timer = setTimeout(() => {
          setForceShowShareWin(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [user]);

  // Listen for WelcomeFlow completion event to show ShareWin
  useEffect(() => {
    const handleWelcomeComplete = () => {
      const pendingShareAction = localStorage.getItem(PENDING_SHARE_ACTION_KEY);
      if (pendingShareAction === 'true') {
        localStorage.removeItem(PENDING_SHARE_ACTION_KEY);
        // Small delay after WelcomeFlow closes
        setTimeout(() => {
          setForceShowShareWin(true);
        }, 800);
      }
    };

    window.addEventListener('welcomeFlowComplete', handleWelcomeComplete);
    return () => window.removeEventListener('welcomeFlowComplete', handleWelcomeComplete);
  }, []);

  // Reset forceShowShareWin when popup is closed
  const handleShareWinClose = () => {
    setForceShowShareWin(false);
  };

  return (
    <div className={`App min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      <Toaster 
        position="top-right"
        offset="80px"
        toastOptions={{
          style: {
            background: theme === 'dark' ? '#1e293b' : 'white',
            color: theme === 'dark' ? '#e2e8f0' : '#1e293b',
            border: `1px solid ${theme === 'dark' ? '#334155' : '#e2e8f0'}`,
            borderRadius: '12px',
          },
        }}
      />
      <BrowserRouter>
        <ScrollToTop />
        <ReferralTracker />
        <PushNotificationPrompt user={user} />
        {/* Welcome Flow for new users - shows above everything when is_new_user is true */}
        <WelcomeFlow />
        <Routes>
          <Route path="/" element={<HomePageWrapper />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/listing/:id" element={<ListingDetailPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/register" element={<AuthPage />} />
          <Route path="/start-selling" element={<StartSellingPage />} />
          {/* OAuth Callback Routes - redirect to AuthPage for processing */}
          <Route path="/auth/facebook/callback" element={<AuthPage />} />
          <Route path="/auth/apple/callback" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/create-listing" element={
            <ProtectedRoute>
              <CreateListingPage />
            </ProtectedRoute>
          } />
          <Route path="/edit-listing/:id" element={
            <ProtectedRoute>
              <CreateListingPage />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/my-listings" element={
            <ProtectedRoute>
              <MyListingsPage />
            </ProtectedRoute>
          } />
          <Route path="/promote/:listingId" element={
            <ProtectedRoute>
              <PromoteListingPage />
            </ProtectedRoute>
          } />
          <Route path="/my-bids" element={
            <ProtectedRoute>
              <MyBidsPage />
            </ProtectedRoute>
          } />
          <Route path="/favorites" element={
            <ProtectedRoute>
              <FavoritesPage />
            </ProtectedRoute>
          } />
          <Route path="/notifications" element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          } />
          <Route path="/currency-converter" element={<CurrencyConverterPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/coming-soon-preview" element={<ComingSoonPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/seller-analytics" element={
            <ProtectedRoute>
              <SellerAnalyticsPage />
            </ProtectedRoute>
          } />
          <Route path="/seller/:userId" element={<SellerProfilePage />} />
          <Route path="/hotspots" element={<HotspotsPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/create" element={
            <ProtectedRoute>
              <CreateEventPage />
            </ProtectedRoute>
          } />
          <Route path="/events/:eventId" element={<EventDetailPage />} />
          <Route path="/events/:eventId/edit" element={
            <ProtectedRoute>
              <CreateEventPage />
            </ProtectedRoute>
          } />
          <Route path="/my-events" element={
            <ProtectedRoute>
              <MyEventsPage />
            </ProtectedRoute>
          } />
          <Route path="/messages" element={
            <ProtectedRoute>
              <MessagesPage />
            </ProtectedRoute>
          } />
          <Route path="/trade-proposals" element={
            <ProtectedRoute>
              <TradeProposalsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin-login" element={<AdminLoginPage />} />
          <Route path="/backoffice" element={<AdminLoginPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/install-app" element={<InstallAppPage />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminPageNew />
            </ProtectedRoute>
          } />
          {/* Safari Dropdown Test Page */}
          <Route path="/safari-test" element={<SafariDropdownTestPage />} />
          <Route path="/safari-test-v2" element={<SafariDropdownTestV2 />} />
          <Route path="/category-test" element={<CategoryTestPage />} />
          <Route path="/hero-options" element={<HeroOptionsPage />} />
          {/* 404 Catch-all Route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <ScrollToTopButton />
        <CookieNotice />
        <ShareWinPopup user={user} forceShow={forceShowShareWin} onClose={handleShareWinClose} />
        <PushNotificationPrompt user={user} />
        <IslandSelectorPopup />
        <InstallAppPopup />
        <MobileBottomNav />
      </BrowserRouter>
    </div>
  );
}

export default App;
